import React from 'react'
import { ArrowRight } from 'lucide-react'
import AiIcon from '@/assets/svg/Ai-Icon'
import { useSearchSuggestions } from '../hooks/useApollo'

const AiIconComponent = AiIcon as React.ComponentType<any>

export interface SuggestionItem {
    label: string
    value: string
}

interface PersonalizedSuggestionsProps {
    /** What is being searched for: people (contacts) or companies (accounts). */
    kind: 'contacts' | 'accounts'
    /** Shown only when the company has no profile to base suggestions on (or the request failed). */
    fallback: SuggestionItem[]
    onPick: (query: string) => void
    limit?: number
}

const SKELETON_WIDTHS = ['w-[460px]', 'w-[400px]', 'w-[430px]']

/**
 * Example searches written from what the user's company sells (an IT services company is
 * shown buyers of IT services). Falls back to a fixed list when there is no company profile.
 */
export default function PersonalizedSuggestions({ kind, fallback, onPick, limit = 3 }: PersonalizedSuggestionsProps) {
    const { suggestions, isLoading, personalized } = (useSearchSuggestions as any)(kind) as {
        suggestions: string[]
        isLoading: boolean
        personalized: boolean
    }

    const items: SuggestionItem[] = personalized
        ? suggestions.slice(0, limit).map((label) => ({ label, value: label }))
        : fallback.slice(0, limit)

    return (
        <div className='flex flex-col gap-4'>
            <h5 className='text-sm font-medium text-gray-400 font-dmMono'>SUGGESTIONS</h5>
            <div className='flex flex-col gap-5' aria-busy={isLoading}>
                {isLoading
                    ? SKELETON_WIDTHS.slice(0, limit).map((width) => (
                          <div key={width} className='flex items-center gap-2 py-1'>
                              <div className='w-4 h-4 rounded bg-gray-200 dark:bg-gray-800 animate-pulse shrink-0' />
                              <div className={`h-3.5 max-w-full rounded bg-gray-200 dark:bg-gray-800 animate-pulse ${width}`} />
                          </div>
                      ))
                    : items.map((item) => (
                          <button
                              key={item.value}
                              type='button'
                              onClick={() => onPick(item.label)}
                              className='flex items-center gap-2 group text-left cursor-pointer transition-colors py-1 w-fit'
                          >
                              <AiIconComponent className='w-4 h-4 shrink-0 text-gray-400 group-hover:text-secondary transition-colors duration-200' />
                              <p className='text-sm font-normal text-gray-900 dark:text-gray-100 group-hover:text-secondary transition-colors duration-200'>
                                  {item.label}
                              </p>
                              <ArrowRight
                                  size={18}
                                  className='shrink-0 text-secondary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out'
                              />
                          </button>
                      ))}
            </div>
        </div>
    )
}
